import React from "react";
import { ClipboardList, Stethoscope, Wrench, PackageCheck, Send, Check, HelpCircle, X, Smartphone, Battery, Zap } from "lucide-react";
import PalcoEscalavel from "../multichannel/PalcoEscalavel";
import { WhatsAppIcon } from "../multichannel/iconesCanais";

/**
 * FluxoCanvasAssistencia — layout dedicado da Assistencia Tecnica.
 *
 * Nicho SEM IA no fluxo. O sistema Evolzap e ferramenta do tecnico
 * (abre OS, registra diagnostico, muda status) e o WhatsApp dispara
 * notificacoes automaticas pro cliente em cada etapa.
 *
 * Storytelling visual: foto do cliente entregando o aparelho,
 * tela do sistema com OS, foto do cliente recebendo o celular pronto.
 *
 * Palco 1500x500 com 8 elementos posicionados absolutamente.
 */

const PALCO_L = 1500;
const PALCO_A = 500;

const CAIXA_BASE = {
  background: "linear-gradient(160deg, rgba(28,33,32,0.95) 0%, rgba(16,20,19,0.98) 100%)",
  border: "1px solid rgba(255,255,255,0.08)",
  borderRadius: "16px",
  boxShadow: "0 20px 44px -18px rgba(0,0,0,0.95)",
};

const CAIXA_ATIVA = {
  ...CAIXA_BASE,
  border: "1px solid rgba(34,197,94,0.5)",
  boxShadow: "0 20px 44px -18px rgba(0,0,0,0.95), 0 0 30px -10px rgba(34,197,94,0.4)",
};

function Card({ x, y, w, h, children, ativa = false, style = {}, className = "", ...resto }) {
  return (
    <div
      className={`absolute overflow-hidden ${className}`.trim()}
      style={{
        left: x, top: y, width: w, height: h,
        ...(ativa ? CAIXA_ATIVA : CAIXA_BASE),
        ...style,
      }}
      {...resto}
    >
      {children}
    </div>
  );
}

export default function FluxoCanvasAssistencia() {
  return (
    <PalcoEscalavel largura={PALCO_L} altura={PALCO_A}>
      {/* Halo de fundo */}
      <div
        className="pointer-events-none absolute rounded-full"
        style={{
          left: 400, top: 100, width: 800, height: 300,
          background: "radial-gradient(ellipse, rgba(34,197,94,0.10) 0%, transparent 68%)",
          filter: "blur(80px)",
        }}
      />

      {/* Conexoes SVG */}
      <svg
        className="absolute inset-0 pointer-events-none"
        width={PALCO_L} height={PALCO_A}
        viewBox={`0 0 ${PALCO_L} ${PALCO_A}`}
        aria-hidden="true"
      >
        {/* Cliente -> Tecnico abre OS */}
        <path className="av-conector" data-ordem={2} d="M210 200 L230 200" stroke="rgba(34,197,94,0.6)" strokeWidth="2" fill="none" />
        <circle className="av-conector" data-ordem={2} cx="220" cy="200" r="3" fill="#22c55e" />

        {/* OS -> Diagnostico */}
        <path className="av-conector" data-ordem={3} d="M510 200 L540 200" stroke="rgba(34,197,94,0.6)" strokeWidth="2" fill="none" />
        <circle className="av-conector" data-ordem={3} cx="525" cy="200" r="3" fill="#22c55e" />

        {/* Diagnostico -> Decisao */}
        <path className="av-conector" data-ordem={4} d="M790 200 L815 200" stroke="rgba(34,197,94,0.5)" strokeWidth="2" strokeDasharray="4 4" fill="none" />

        {/* Decisao -> Reparo em execucao (SIM) */}
        <path className="av-conector" data-ordem={6} d="M985 200 L1010 200" stroke="rgba(34,197,94,0.5)" strokeWidth="2" strokeDasharray="4 4" fill="none" />

        {/* Reparo -> Cliente retira */}
        <path className="av-conector" data-ordem={8} d="M1220 200 L1240 200" stroke="rgba(34,197,94,0.6)" strokeWidth="2" fill="none" />
        <circle className="av-conector" data-ordem={8} cx="1230" cy="200" r="3" fill="#22c55e" />

        {/* Decisao -> Cliente recusa (NAO) */}
        <path className="av-conector" data-ordem={5} d="M900 285 Q 900 330, 780 360" stroke="rgba(239,68,68,0.55)" strokeWidth="2" strokeDasharray="5 5" fill="none" />

        {/* Reparo -> Notificacao "Pronto!" */}
        <path className="av-conector" data-ordem={7} d="M1115 320 L1115 360" stroke="rgba(34,197,94,0.5)" strokeWidth="2" strokeDasharray="4 4" fill="none" />
        <polygon className="av-conector" data-ordem={7} points="1109,355 1115,365 1121,355" fill="rgba(34,197,94,0.7)" />

        {/* Pill SIM */}
        <rect className="av-conector" data-ordem={6} x="975" y="180" width="34" height="20" rx="10" fill="rgba(15,20,18,0.95)" stroke="rgba(34,197,94,0.6)" strokeWidth="1" />
        <text className="av-conector" data-ordem={6} x="992" y="194" fontSize="11" fontWeight="700" fill="#6ee7a0" textAnchor="middle">Sim</text>

        {/* Pill NAO */}
        <rect className="av-conector" data-ordem={5} x="855" y="305" width="42" height="22" rx="11" fill="rgba(15,20,18,0.95)" stroke="rgba(239,68,68,0.6)" strokeWidth="1" />
        <text className="av-conector" data-ordem={5} x="876" y="320" fontSize="11" fontWeight="700" fill="#fca5a5" textAnchor="middle">Não</text>
      </svg>

      {/* === 1. CLIENTE chega na loja === */}
      <div className="absolute av-bloco" data-ordem={1} style={{ left: 20, top: 40, width: 200, height: 360 }}>
        <div className="relative mx-auto" style={{ width: 150, height: 150 }}>
          <div
            className="h-full w-full overflow-hidden rounded-full"
            style={{
              border: "3px solid rgba(34,197,94,0.3)",
              boxShadow: "0 12px 30px -8px rgba(0,0,0,0.7), 0 0 30px -8px rgba(34,197,94,0.25)",
            }}
          >
            <img src="/foto-cliente-assistencia.webp" alt="" loading="lazy" className="h-full w-full object-cover" />
          </div>
          <div className="absolute -top-1 -left-1 flex h-10 w-10 items-center justify-center rounded-full bg-[#25D366] shadow-lg">
            <WhatsAppIcon className="h-6 w-6 text-white" />
          </div>
        </div>
        <div className="mt-4 text-center">
          <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">Cliente</div>
          <div className="mt-1 text-[16px] font-bold text-white">Chega na loja</div>
        </div>
        <div className="mt-4 rounded-xl px-3 py-2.5" style={CAIXA_BASE}>
          <div className="text-[11.5px] text-zinc-300 leading-snug text-center">
            "Meu celular não está carregando"
          </div>
        </div>
      </div>

      {/* === 2. TECNICO abre OS no sistema (tela do software) === */}
      <Card className="av-bloco" data-ordem={2} x={230} y={80} w={280} h={240}>
        <div className="flex flex-col h-full p-4">
          <div className="flex items-center gap-2 mb-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-500/15 border border-blue-500/30">
              <ClipboardList className="h-4 w-4 text-blue-300" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[9.5px] font-bold uppercase tracking-[0.14em] text-blue-400">Técnico</div>
              <div className="text-[14px] font-bold text-white leading-tight">Abre OS no sistema</div>
            </div>
          </div>
          <div className="rounded-lg bg-white/[0.04] border border-white/10 p-2.5 flex flex-col gap-1.5">
            <div className="flex items-center justify-between">
              <span className="text-[9.5px] text-zinc-500 uppercase tracking-wider">OS</span>
              <span className="text-[11px] font-bold text-white">#2048</span>
            </div>
            <div className="h-px bg-white/10" />
            <div className="flex items-center gap-1.5">
              <Smartphone className="h-3 w-3 text-zinc-500" />
              <span className="text-[10.5px] text-zinc-300">iPhone 12</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Zap className="h-3 w-3 text-zinc-500" />
              <span className="text-[10.5px] text-zinc-300">Não carrega</span>
            </div>
            <div className="mt-1 flex items-center gap-1.5 px-1.5 py-1 rounded bg-blue-500/15 border border-blue-500/25">
              <span className="h-1.5 w-1.5 rounded-full bg-blue-400 animate-pulse" />
              <span className="text-[9.5px] font-bold text-blue-300 uppercase tracking-wider">Aberto</span>
            </div>
          </div>
        </div>
      </Card>

      {/* === 3. DIAGNOSTICO do aparelho (checklist do tecnico) === */}
      <Card className="av-bloco" data-ordem={3} x={540} y={80} w={250} h={240}>
        <div className="flex flex-col h-full p-4">
          <div className="flex items-center gap-2.5 mb-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-green-500/15 border border-green-500/30">
              <Stethoscope className="h-4 w-4 text-green-400" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[14px] font-bold text-white leading-tight">Diagnóstico</div>
              <div className="text-[10px] text-zinc-500 mt-0.5">Bancada · OS #2048</div>
            </div>
          </div>
          <div className="flex flex-col gap-1.5">
            {[
              { nome: "Bateria", ok: true, icone: Battery },
              { nome: "Conector de carga", ok: false, icone: Zap },
              { nome: "Placa lógica", ok: true, icone: Check },
            ].map((item) => {
              const Ic = item.icone;
              return (
                <div key={item.nome} className="flex items-center justify-between rounded-md bg-white/5 border border-white/10 px-2.5 py-1.5">
                  <div className="flex items-center gap-1.5">
                    <Ic className="h-3 w-3 text-zinc-500" />
                    <span className="text-[11px] text-zinc-300">{item.nome}</span>
                  </div>
                  {item.ok ? (
                    <span className="text-[9.5px] font-medium text-green-400">OK</span>
                  ) : (
                    <span className="text-[9.5px] font-medium text-amber-400">FALHA</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </Card>

      {/* === 4. DECISAO — LOSANGO === */}
      <div
        className="absolute av-bloco"
        data-ordem={4}
        style={{
          left: 840, top: 140, width: 120, height: 120,
          transform: "rotate(45deg)",
          background: "linear-gradient(160deg, rgba(28,33,32,0.95) 0%, rgba(16,20,19,0.98) 100%)",
          border: "1px solid rgba(245,158,11,0.5)",
          borderRadius: "12px",
          boxShadow: "0 20px 44px -18px rgba(0,0,0,0.95), 0 0 25px -8px rgba(245,158,11,0.35)",
        }}
      >
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 p-2" style={{ transform: "rotate(-45deg)" }}>
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-amber-500/15 border border-amber-500/30">
            <HelpCircle className="h-4 w-4 text-amber-400" />
          </div>
          <div className="text-[8.5px] font-bold uppercase tracking-[0.14em] text-amber-400">Decisão</div>
          <div className="text-[10px] font-bold text-white leading-tight text-center">Cliente<br />autoriza?</div>
        </div>
      </div>

      {/* === 5. REPARO em execucao (ATIVA verde) === */}
      <Card className="av-bloco" data-ordem={6} x={1010} y={80} w={210} h={240} ativa>
        <div className="flex flex-col h-full p-3.5">
          <div className="flex items-center gap-2 mb-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-green-500/25 border border-green-500/50">
              <Wrench className="h-4 w-4 text-green-300" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[9.5px] font-bold uppercase tracking-[0.14em] text-green-400">Reparo</div>
              <div className="text-[13px] font-bold text-white leading-tight">Em execução</div>
            </div>
          </div>
          <div className="text-[10px] text-zinc-400 leading-snug mb-3">Troca do conector de carga.</div>
          <div className="mt-auto rounded-lg bg-white/5 border border-white/10 px-2.5 py-2">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[10.5px] text-zinc-400">Status</span>
              <span className="flex h-4 w-4 items-center justify-center rounded-full bg-green-500">
                <Check className="h-3 w-3 text-black" strokeWidth={3} />
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-[13px] font-bold text-white">Autorizado</span>
              <span className="ml-auto px-1.5 py-0.5 rounded bg-green-500/25 text-[9px] font-bold text-green-300 uppercase">R$ 180</span>
            </div>
          </div>
        </div>
      </Card>

      {/* === 6. CLIENTE RETIRA (foto + selo) === */}
      <div className="absolute av-bloco" data-ordem={8} style={{ left: 1240, top: 40, width: 250, height: 360 }}>
        <div className="absolute" style={{ left: 0, top: 100, width: 90 }}>
          <div className="text-[14px] font-bold text-white leading-tight mb-2">Cliente retira</div>
          <div className="text-[10.5px] text-zinc-400 leading-snug">Aparelho pronto e cliente notificado.</div>
        </div>
        <div
          className="absolute overflow-hidden"
          style={{
            left: 100, top: 0, width: 150, height: 340,
            borderRadius: "16px",
            border: "1px solid rgba(255,255,255,0.08)",
            boxShadow: "0 20px 44px -18px rgba(0,0,0,0.95)",
          }}
        >
          <img src="/foto-celular-consertado.webp" alt="" loading="lazy" className="h-full w-full object-cover" />
          <div className="absolute bottom-2 left-2 right-2 rounded-lg bg-black/85 backdrop-blur-sm border border-green-500/30 px-2 py-1.5">
            <div className="flex items-center gap-1.5">
              <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-green-500">
                <PackageCheck className="h-2.5 w-2.5 text-black" strokeWidth={3} />
              </span>
              <span className="text-[10px] font-bold text-white leading-tight">OS #2048</span>
            </div>
            <div className="text-[9px] text-zinc-400 mt-0.5">Retirado 🔧</div>
          </div>
        </div>
      </div>

      {/* === 7. CLIENTE RECUSA (ramo NAO) === */}
      <Card className="av-bloco" data-ordem={5} x={510} y={360} w={380} h={130}>
        <div className="flex flex-col h-full p-3">
          <div className="flex items-center gap-2 mb-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/5 border border-white/10">
              <X className="h-3.5 w-3.5 text-zinc-400" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[13px] font-bold text-white leading-tight">Cliente recusa</div>
              <div className="text-[10px] text-zinc-500 mt-0.5">Aparelho devolvido sem intervenção.</div>
            </div>
          </div>
          <div className="rounded-lg bg-white/5 border border-white/10 px-2.5 py-2 mt-auto">
            <div className="text-[10.5px] text-zinc-300 leading-snug">
              Sistema encerra a OS e envia comprovante ao cliente pelo WhatsApp automaticamente.
            </div>
          </div>
        </div>
      </Card>

      {/* === 8. NOTIFICACAO automatica pro cliente === */}
      <Card className="av-bloco" data-ordem={7} x={920} y={360} w={380} h={130}>
        <div className="flex flex-col h-full p-3">
          <div className="flex items-center gap-2 mb-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#25D366]/20 border border-[#25D366]/40">
              <Send className="h-4 w-4 text-[#25D366]" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[13px] font-bold text-white leading-tight">Aviso automático no WhatsApp</div>
              <div className="text-[10px] text-zinc-500 mt-0.5">Disparado a cada mudança de status da OS.</div>
            </div>
          </div>
          <div className="rounded-lg bg-[#005c4b]/60 border border-[#25D366]/30 px-2.5 py-2 mt-auto">
            <div className="text-[10.5px] text-white leading-snug">
              "Seu iPhone está pronto! Pode passar pra buscar 🔧 — OS #2048"
            </div>
            <div className="flex items-center gap-1 mt-1 justify-end">
              <span className="text-[9px] text-zinc-400">enviado agora</span>
              <Check className="h-2.5 w-2.5 text-[#53bdeb]" strokeWidth={3} />
              <Check className="h-2.5 w-2.5 text-[#53bdeb] -ml-1.5" strokeWidth={3} />
            </div>
          </div>
        </div>
      </Card>
    </PalcoEscalavel>
  );
}
